/**
 * « Vous n'êtes pas membre de ce projet » et « Ce projet est archivé » — lot NAV 0.5.x (registre
 * n° 11 ; A2 § 2.2, constat 7 ; A3 G15). Rendus par `PorteProjet` à la place de l'écran demandé.
 *
 * Non membre : la personne voit son état de demande (aucune, en attente, refusée avec le motif,
 * accordée) et peut DEMANDER l'accès avec un message, ou annuler sa demande. L'administrateur de la
 * plateforme, lui, REJOINT le projet (geste existant, tracé). Le nom du projet n'est jamais révélé à
 * un non-membre (§ 4.7 : les projets non ouverts sont comptés, jamais nommés) : seul son identifiant,
 * déjà dans l'adresse, est rappelé.
 *
 * Introuvable (AUD-CRAWL-P9) : l'identifiant n'est pas un projet de l'organisation — rien à demander.
 */
import { useState, type FormEvent, type JSX } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Lock, SearchX } from 'lucide-react'

import { ApiError, type Workspace } from '@/api/client'
import { demandesAccesApi, type DemandeAcces } from '@/api/demandesAccesApi'
import { codeErreur } from '@/api/niveauxApi'
import { GestesCycleProjetActif } from '@/components/cycle/ActionsCycleProjet'
import type { Session } from '@/auth/session'
import { ErrorState, LoadingState } from '@/components/ErrorState'
import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useRejoindreProjet } from '@/hooks/useRejoindreProjet'
import { useT } from '@/i18n/language'
import { LIEN_CIBLE } from '@/lib/cibles'

/** La demande la plus récente de la personne pour ce projet, ou `null`. */
export function derniereDemande(demandes: readonly DemandeAcces[] | undefined, workspaceId: string): DemandeAcces | null {
  const miennes = (demandes ?? []).filter((d) => d.workspace_id === workspaceId)
  if (miennes.length === 0) return null
  return [...miennes].sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : b.id - a.id))[0]!
}

function messageErreurDemande(e: unknown, t: (k: string, r: string) => string): string {
  switch (codeErreur(e)) {
    case 'ACCESS_REQUEST_ALREADY_PENDING': return t('nonMembre.dejaEnAttente', 'Une demande attend déjà la décision d’un propriétaire.')
    case 'ACCESS_REQUEST_ALREADY_MEMBER': return t('nonMembre.dejaMembre', 'Vous êtes déjà membre de ce projet : rechargez la page.')
    case 'WORKSPACE_ARCHIVED': return t('nonMembre.archive', 'Ce projet est archivé : il n’accueille plus de nouveaux membres.')
    case 'ACCESS_REQUEST_USER_UNKNOWN': return t('nonMembre.compteInconnu', 'Votre compte n’est pas un utilisateur actif de cette organisation.')
    default:
      if (e instanceof ApiError && e.status === 404) return t('nonMembre.introuvable', 'Ce projet est introuvable dans votre organisation.')
      return e instanceof Error ? e.message : String(e)
  }
}

export function ProjetNonMembre({ workspaceId, session, aRejoindre, dansOrganisation = false }: {
  workspaceId: string
  session: Session
  /** Projet que l'administrateur de la plateforme peut rejoindre (sinon `null`). */
  aRejoindre: Workspace | null
  /**
   * Le projet figure dans la liste COMPLÈTE de l'organisation (administrateur de la plateforme) : il lui
   * appartient à coup sûr — et, s'il n'est pas « à rejoindre », c'est qu'il est archivé. Sinon (porteur
   * ordinaire : la passerelle ne liste que ses projets) l'appartenance n'est pas connue et l'écran ne
   * l'affirme pas — AUD-CRAWL-P9.
   */
  dansOrganisation?: boolean
}): JSX.Element {
  const { t, formatDate } = useT()
  const queryClient = useQueryClient()
  const [message, setMessage] = useState('')
  const rejoindre = useRejoindreProjet()
  const adminPlateforme = session.roles.includes('admin')

  const mesDemandes = useQuery({
    // Clé portée par l'IDENTITÉ (les demandes sont celles de l'appelant).
    queryKey: ['mes-demandes-acces', session.userId],
    queryFn: demandesAccesApi.mesDemandes,
    enabled: !adminPlateforme,
    // Tant qu'une demande attend, sa décision est relue : l'accès accordé ouvre le projet sans recharger.
    refetchInterval: (q) => (q.state.data?.some((d) => d.workspace_id === workspaceId && d.status === 'pending') ? 15_000 : false),
  })
  const derniere = derniereDemande(mesDemandes.data, workspaceId)
  const rafraichir = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['mes-demandes-acces'] })
    void queryClient.invalidateQueries({ queryKey: ['workspaces'] })
  }
  const demander = useMutation({
    mutationFn: () => demandesAccesApi.demander(workspaceId, message.trim() || undefined),
    onSuccess: () => { setMessage(''); rafraichir() },
    meta: { retour: 'insitu' },
  })
  const annuler = useMutation({
    mutationFn: (id: number) => demandesAccesApi.annuler(id),
    onSuccess: rafraichir,
    meta: { retour: 'insitu' },
  })

  const soumettre = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    demander.mutate()
  }

  return (
    <section className="mx-auto max-w-2xl space-y-4" data-testid="projet-non-membre" data-projet={workspaceId}>
      <PageHeader icon={Lock} title={t('nonMembre.titre', 'Vous n’êtes pas membre de ce projet')}
                  description={dansOrganisation
                    ? t('nonMembre.description', 'Le projet {{id}} appartient à votre organisation, mais vous n’y avez aucun rôle : son contenu ne vous est pas montré.', { id: workspaceId })
                    : t('nonMembre.descriptionNeutre', 'Vous n’avez aucun rôle dans le projet {{id}} : son contenu ne vous est pas montré. S’il fait partie de votre organisation, vous pouvez en demander l’accès à ses propriétaires.', { id: workspaceId })} />

      {adminPlateforme ? (
        <Card>
          <CardContent className="space-y-3 pt-6 text-sm">
            {aRejoindre ? (
              <>
                <p>{t('nonMembre.adminRejoindre', 'Administrateur de la plateforme, vous pouvez rejoindre « {{nom}} » comme propriétaire : le geste est tracé au journal.', { nom: aRejoindre.name })}</p>
                <Button type="button" data-testid="non-membre-rejoindre" disabled={rejoindre.enCours !== null}
                        onClick={() => void rejoindre.rejoindre(aRejoindre.workspace_id, aRejoindre.name)}>
                  {t('nonMembre.rejoindre', 'Rejoindre ce projet')}
                </Button>
                {rejoindre.message ? <p role="status">{rejoindre.message}</p> : null}
                {rejoindre.erreur ? <p role="alert" className="text-[var(--color-error-text)]">{rejoindre.erreur}</p> : null}
              </>
            ) : (
              <p role="status" data-testid="non-membre-admin-indisponible">
                {/* « À rejoindre » = les projets de l'organisation sans appartenance, ARCHIVÉS exclus
                    (`WorkspaceProvider`). Un projet que la liste complète déclare de l'organisation (le
                    cas d'ici, depuis AUD-CRAWL-P9) et qui n'y figure pas est donc archivé — rien d'autre
                    ne le retire ; « ou n'appartient pas à votre organisation » contredisait l'en-tête. */}
                {dansOrganisation
                  ? t('nonMembre.adminArchive', 'Ce projet est archivé : il n’est pas proposé au rattachement.')
                  : t('nonMembre.adminIndisponible', 'Ce projet n’est pas proposé au rattachement : il est archivé ou n’appartient pas à votre organisation.')}
              </p>
            )}
          </CardContent>
        </Card>
      ) : mesDemandes.isPending ? (
        <LoadingState label={t('nonMembre.chargement', 'Lecture de vos demandes d’accès…')} />
      ) : mesDemandes.isError ? (
        <ErrorState message={t('nonMembre.erreurLecture', 'Vos demandes d’accès n’ont pas pu être lues.')}
                    detail={(mesDemandes.error as Error).message} onRetry={() => void mesDemandes.refetch()} />
      ) : derniere?.status === 'pending' ? (
        <Card data-testid="non-membre-en-attente">
          <CardContent className="space-y-3 pt-6 text-sm">
            <p role="status">
              {t('nonMembre.enAttente', 'Demande envoyée le {{date}} : elle attend la décision d’un propriétaire du projet, qui en a été notifié.', { date: formatDate(derniere.created_at) })}
            </p>
            {derniere.message ? <p className="text-muted-foreground">« {derniere.message} »</p> : null}
            <Button type="button" variant="outline" data-testid="non-membre-annuler" disabled={annuler.isPending}
                    onClick={() => annuler.mutate(derniere.id)}>
              {t('nonMembre.annuler', 'Annuler ma demande')}
            </Button>
            {annuler.isError ? <p role="alert" className="text-[var(--color-error-text)]">{messageErreurDemande(annuler.error, t)}</p> : null}
          </CardContent>
        </Card>
      ) : derniere?.status === 'approved' ? (
        <Card data-testid="non-membre-accordee">
          <CardContent className="space-y-3 pt-6 text-sm">
            <p role="status">{t('nonMembre.accordee', 'Votre demande est accordée : le projet s’ouvre dès que la liste de vos projets est relue.')}</p>
            <Button type="button" onClick={rafraichir}>{t('nonMembre.ouvrir', 'Ouvrir le projet')}</Button>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="non-membre-demande">
          <CardContent className="space-y-3 pt-6 text-sm">
            {derniere?.status === 'rejected' ? (
              <p role="status" data-testid="non-membre-refusee">
                {t('nonMembre.refusee', 'Votre demande du {{date}} a été refusée', { date: formatDate(derniere.created_at) })}
                {derniere.decision_reason ? ` : « ${derniere.decision_reason} »` : '.'}{' '}
                {t('nonMembre.redemander', 'Vous pouvez en déposer une nouvelle.')}
              </p>
            ) : null}
            <form className="space-y-3" onSubmit={soumettre}>
              <div className="space-y-1.5">
                <Label htmlFor="demande-acces-message">{t('nonMembre.messageLabel', 'Message au propriétaire (facultatif)')}</Label>
                <Textarea id="demande-acces-message" data-testid="non-membre-message" maxLength={1000} value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder={t('nonMembre.messageExemple', 'Pourquoi avez-vous besoin de ce projet ?')} />
              </div>
              <Button type="submit" data-testid="non-membre-demander" disabled={demander.isPending}>
                {t('nonMembre.demander', 'Demander l’accès')}
              </Button>
            </form>
            {demander.isError ? (
              <p role="alert" className="text-[var(--color-error-text)]" data-testid="non-membre-erreur">{messageErreurDemande(demander.error, t)}</p>
            ) : null}
          </CardContent>
        </Card>
      )}

      <p className="text-sm">
        <Link to="/org/projets" className={LIEN_CIBLE} data-testid="non-membre-mes-projets">{t('nonMembre.mesProjets', 'Revenir à mes projets')}</Link>
      </p>
    </section>
  )
}

/**
 * AUD-CRAWL-P9 (audit du 2026-10-09) : l'adresse désigne un projet ABSENT de la liste COMPLÈTE de
 * l'organisation (celle de l'administrateur de la plateforme, cf. `PorteProjet`) — inconnu, supprimé, ou
 * d'une autre organisation (la passerelle ne les distingue pas, et n'a pas à le faire). L'écran du
 * non-membre affirmait « appartient à votre organisation » et proposait une demande d'accès vouée au 404 :
 * ici, ni l'un ni l'autre ; seul l'identifiant, déjà dans l'adresse, est rappelé.
 */
export function ProjetIntrouvable({ workspaceId }: { workspaceId: string }): JSX.Element {
  const { t } = useT()
  return (
    <section className="mx-auto max-w-2xl space-y-4" data-testid="projet-introuvable" data-projet={workspaceId}>
      <PageHeader icon={SearchX} title={t('nonMembre.introuvableTitre', 'Ce projet est introuvable dans votre organisation')}
                  description={t('nonMembre.introuvableDescription', 'Aucun projet de votre organisation ne porte l’identifiant {{id}} : le lien est erroné ou périmé, ou il désigne un projet d’une autre organisation.', { id: workspaceId })} />
      <Card>
        <CardContent className="space-y-2 pt-6 text-sm">
          <p>{t('projetArchive.choisir', 'Choisissez un autre projet dans le sélecteur de contexte, ou depuis la liste des projets de l’organisation.')}</p>
          <p><Link to="/org/projets" className={LIEN_CIBLE} data-testid="projet-introuvable-mes-projets">{t('nonMembre.mesProjets', 'Revenir à mes projets')}</Link></p>
        </CardContent>
      </Card>
    </section>
  )
}

export function ProjetArchive({ projet }: { projet: Workspace }): JSX.Element {
  const { t } = useT()
  return (
    <section className="mx-auto max-w-2xl space-y-4" data-testid="projet-archive" data-projet={projet.workspace_id}>
      <PageHeader icon={Archive} title={t('projetArchive.titre', 'Ce projet est archivé')}
                  description={t('projetArchive.description', '« {{nom}} » n’est plus un lieu de travail : la passerelle ne sert plus son contenu, et ses écrans ne sont pas affichés.', { nom: projet.name })} />
      <Card>
        <CardContent className="space-y-2 pt-6 text-sm">
          <p>{t('projetArchive.choisir', 'Choisissez un autre projet dans le sélecteur de contexte, ou depuis la liste des projets de l’organisation.')}</p>
          <p><Link to="/org/projets" className={LIEN_CIBLE} data-testid="projet-archive-mes-projets">{t('nonMembre.mesProjets', 'Revenir à mes projets')}</Link></p>
        </CardContent>
      </Card>
      {/* Intégration CYC × NAV : le propriétaire et l'administration de l'organisation désarchivent
          ICI (gestes du lot CYC ; rien pour les autres rôles). */}
      <div data-testid="projet-archive-gestes"><GestesCycleProjetActif projet={projet} /></div>
    </section>
  )
}
